import { CheckIcon } from "@wags/ui/product-icons.tsx";

import type { ReactNode } from "react";
import { PawGlyph } from "./paw-glyph";
import { IconSlot } from "@/components/shared/IconSlot";
import { TINTED_PANEL_CLASS } from "@/components/shared/tintedPanel";
import { sentenceCaseFactLabel } from "../profile/fact-label";
import { factState } from "../profile/fact-state";
import { hasTrustFactsContent } from "../profile/section-content";
import type { Fact, FactCategory, ProviderProfileV2 } from "../profile/types";

const UNKNOWN_LIST = new Intl.ListFormat("en", { type: "disjunction" });
const NOT_REQUIRED_LIST = new Intl.ListFormat("en", { type: "conjunction" });

export function TrustFactsSection({
    profile,
    excludeCategories = [],
    askAction,
}: {
    profile: ProviderProfileV2;

    excludeCategories?: FactCategory[];
    askAction?: { icon: ReactNode; onClick: () => void };
}) {
    const groups = profile.factGroups.filter(
        (group) => !excludeCategories.includes(group.category),
    );

    const stated = (fact: Fact) => factState(fact, { noteCounts: true });

    const statedGroups = groups
        .map((group) => ({
            category: group.category,
            label: group.label,
            facts: group.facts.filter((fact) => stated(fact) === "yes"),
        }))
        .filter((group) => group.facts.length > 0);

    const labelsFor = (state: ReturnType<typeof stated>) => [
        ...new Set(
            groups
                .flatMap((group) => group.facts)
                .filter((fact) => stated(fact) === state)
                .map((fact) => sentenceCaseFactLabel(fact.label)),
        ),
    ];
    const unknownLabels = labelsFor("unknown");

    const notRequiredLabels = labelsFor("no");

    if (!hasTrustFactsContent(profile, excludeCategories)) return null;

    const unknownList = UNKNOWN_LIST.format(unknownLabels);
    const notRequiredList = NOT_REQUIRED_LIST.format(notRequiredLabels);

    return (
        <section className="lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start lg:gap-x-16">
            <div>
                <h2 className="text-section-title text-doberman-black text-balance">
                    Things to know
                </h2>
                <p className="mt-3 text-body-small text-doberman-black/50">
                    Sourced from the provider&rsquo;s website and Google. Free
                    listing, no paid placement.
                </p>
            </div>

            <div className="mt-5 lg:mt-0 lg:max-w-190">
                <div className="sm:columns-2 sm:gap-x-4">
                    {statedGroups.map((group) => (
                        <div
                            key={group.category}
                            className="break-inside-avoid pb-4"
                        >
                            <div className={TINTED_PANEL_CLASS}>
                                <p className="text-body font-semibold text-doberman-black">
                                    {group.label}
                                </p>
                                <div className="mt-1.5">
                                    {group.facts.map((fact, index) => (
                                        <div
                                            key={fact.key}
                                            className={`flex items-start gap-3 py-2.5 last:pb-0${
                                                index > 0
                                                    ? " border-t border-doberman-black/8"
                                                    : ""
                                            }`}
                                        >
                                            {fact.value === true ? (
                                                <IconSlot className="text-doberman-black/40">
                                                    <CheckIcon
                                                        className="size-4"
                                                        aria-hidden="true"
                                                    />
                                                </IconSlot>
                                            ) : (
                                                <IconSlot>
                                                    <span
                                                        aria-hidden="true"
                                                        className="size-1.5 rounded-full bg-doberman-black/25"
                                                    />
                                                </IconSlot>
                                            )}
                                            <span className="text-body text-doberman-black/85">
                                                {fact.note ?? fact.label}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {notRequiredLabels.length > 0 && (
                    <p className="pb-4 text-body text-doberman-black/70">
                        {notRequiredList} — not required.
                    </p>
                )}

                {unknownLabels.length > 0 && (
                    <div className="flex flex-col items-start gap-3 rounded-card bg-havanese p-card md:flex-row md:items-center md:justify-between md:gap-4">
                        <div className="flex items-start gap-3">
                            <PawGlyph />
                            <div>
                                <p className="text-body text-doberman-black/85">
                                    We couldn&rsquo;t confirm {unknownList}.
                                </p>
                                <p className="mt-1 text-body-small text-doberman-black/55">
                                    Not a no, just unconfirmed.
                                </p>
                            </div>
                        </div>
                        {askAction && (
                            <button
                                type="button"
                                onClick={askAction.onClick}
                                className="flex shrink-0 min-h-11 items-center gap-2 self-start rounded-full bg-doberman-black py-1.5 pl-2 pr-4 transition-transform duration-150 active:scale-97 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow focus-visible:ring-offset-2 md:self-center"
                            >
                                {askAction.icon}
                                <span className="font-family-heading text-[15px] text-true-white">
                                    Ask Kits
                                </span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
